import React, { useEffect, useMemo, useState } from 'react';
import { Card, Button, TextField, Alert } from '@dopomogai/ui';
import { Plus, Trash2 } from 'lucide-react';
import SecretTokenPicker from './SecretTokenPicker';

export interface HeaderEditorProps {
  headers: Record<string, string>;
  onChange: (next: Record<string, string>) => void;
}

type Row = { key: string; value: string };

export const HeaderEditor: React.FC<HeaderEditorProps> = ({ headers, onChange }) => {
  const [rows, setRows] = useState<Row[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const r: Row[] = Object.entries(headers || {}).map(([k, v]) => ({ key: k, value: v }));
    setRows(r);
  }, [headers]);

  const validate = (): string | null => {
    const keys = new Set<string>();
    for (const r of rows) {
      const k = (r.key || '').trim();
      if (!k) return 'Header key is required.';
      if (keys.has(k)) return `Duplicate header: ${k}`;
      keys.add(k);
    }
    return null;
  };

  const apply = () => {
    const e = validate();
    if (e) {
      setError(e);
      return;
    }
    setError(null);
    const next: Record<string, string> = {};
    rows.forEach(r => {
      if (r.key.trim()) next[r.key.trim()] = r.value ?? '';
    });
    onChange(next);
  };

  const addRow = () => setRows(prev => [...prev, { key: '', value: '' }]);
  const removeRow = (idx: number) => {
    const next = rows.slice();
    next.splice(idx, 1);
    setRows(next);
  };

  const updateRow = (idx: number, part: Partial<Row>) => {
    const next = rows.slice();
    next[idx] = { ...next[idx], ...part };
    setRows(next);
  };

  return (
    <Card className="bg-white border-2 border-black" noPadding={false}>
      <div className="space-y-3">
        {error && <Alert type="error" message={error} />}
        <div className="space-y-2">
          {rows.map((row, idx) => (
            <div key={idx} className="grid grid-cols-12 gap-2 items-end">
              <div className="col-span-3">
                <TextField label="Header" value={row.key} onChange={(e) => updateRow(idx, { key: e.target.value })} placeholder="Authorization" />
              </div>
              <div className="col-span-8">
                <TextField
                  label="Value"
                  value={row.value}
                  onChange={(e) => updateRow(idx, { value: e.target.value })}
                  placeholder="Bearer ${secret:MY_TOKEN}"
                />
              </div>
              <div className="col-span-1 flex justify-end">
                <Button variant="ghost" size="sm" icon={<Trash2 className="w-4 h-4" />} onClick={() => removeRow(idx)} />
              </div>
              <div className="col-span-12">
                <SecretTokenPicker
                  onInsert={(token) => updateRow(idx, { value: (row.value || '') + token })}
                />
              </div>
            </div>
          ))}
        </div>
        <div className="flex items-center gap-2">
          <Button variant="secondary" size="sm" icon={<Plus className="w-4 h-4" />} onClick={addRow}>
            Add header
          </Button>
          <Button variant="primary" size="sm" onClick={apply}>Apply</Button>
        </div>
      </div>
    </Card>
  );
};

export default HeaderEditor;
